import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Box,
  Typography,
  Button,
  SvgIcon,
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import ModalDialogVideo from 'src/components/ModalDialogVideo/index';
import { STYLE_OPTIONS } from 'src/constants';

const webinarReplayBannerStyles = {
  replayCard: {
    padding: '24px',
    marginBottom: '20px',
    background: 'linear-gradient(135deg, #db2777 0%, #be185d 50%, #9d174d 100%)',
    color: '#fff',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: STYLE_OPTIONS.FONT_FAMILY,

    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-50%',
      right: '-50%',
      width: '200%',
      height: '200%',
      background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
      zIndex: 0,
    },
  },

  contentWrapper: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px',
  },

  textContent: {
    flex: '1 1 300px',
    flexShrink: 1,
    minWidth: 0,
  },

  titleRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '6px',
  },

  title: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: '24px',
    fontWeight: 'bold',
    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
  },

  subtitle: {
    flexShrink: 1,
    fontSize: '15px',
    opacity: 0.9,
    lineHeight: 1.4,
  },

  buttonContent: {
    flex: '0 0 auto',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },

  videoButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#fff',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    padding: '12px 24px',
    borderRadius: '50px',
    fontSize: '16px',
    fontWeight: 'bold',
    textTransform: 'none',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',

    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      border: '2px solid rgba(255, 255, 255, 0.5)',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
    },
  },

  replayBadge: {
    flexShrink: 0,
    backgroundColor: '#fff',
    color: '#9d174d',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
  },

  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
};

// Unlisted Vimeo video: the h= privacy hash is required for it to play.
const WEBINAR_REPLAY_VIDEO_SRC = 'https://player.vimeo.com/video/1177930705?h=79eba6e8cb';

function WebinarReplayBanner({ classes }) {
  const [replayOpen, setReplayOpen] = useState(false);

  const handleOpenReplay = () => setReplayOpen(true);
  const handleCloseReplay = () => setReplayOpen(false);

  return (
    <>
      <Card className={classes.replayCard}>
        <Box className={classes.contentWrapper}>
          <Box className={classes.textContent}>
            <Box className={classes.titleRow}>
              <Typography className={classes.title}>
                🎬 What&apos;s New in CP Since Summer: October 2026 Webinar Replay
              </Typography>
              <Box className={classes.replayBadge}>
                Replay
              </Box>
            </Box>
            <Typography className={classes.subtitle}>
              Catch up on everything we&apos;ve added since summer, plus real consultants
              sharing how they&apos;re getting ready for the holidays. Click the chapters list
              icon in the video to jump to any topic.
            </Typography>
          </Box>
          <Box className={classes.buttonContent}>
            <Button
              className={classes.videoButton}
              onClick={handleOpenReplay}
              size="large"
            >
              <Box className={classes.iconWrapper}>
                <SvgIcon component={PlayArrowIcon} />
                Watch the Replay
                <SvgIcon component={OndemandVideoIcon} />
              </Box>
            </Button>
          </Box>
        </Box>
      </Card>

      <ModalDialogVideo
        onClose={handleCloseReplay}
        isVisible={replayOpen}
        videosrc={WEBINAR_REPLAY_VIDEO_SRC}
        title="What's New in CP Since Summer: October 2026 Webinar Replay"
        largeModal
      />
    </>
  );
}

WebinarReplayBanner.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(WebinarReplayBanner, webinarReplayBannerStyles);
